import { Box, Text, VisuallyHidden } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { useEffect, useRef, useState } from "react";

import {
  ADVISOR_KIN_MINT_100,
  ADVISOR_KIN_MINT_200,
  ADVISOR_KIN_MINT_400,
  ADVISOR_PANEL_NEUTRAL_250,
  ADVISOR_PANEL_PRIMARY_500,
} from "./advisor-panel-tokens";
import type { ToolProgressItem } from "./tool-progress";
import { primaryToolProgressItem } from "./tool-progress-visibility";

export type AgentToolProgressProps = {
  items: ToolProgressItem[];
};

const FAILED_COPY = "Couldn't complete that lookup";
/** Soft danger that stays quieter than system-error red.700 bubbles. */
const FAILED_ACCENT = "#C45C4A";
/** Fast exit so a replaced status crossfades without a hard snap. */
export const KIN_TOOL_PROGRESS_EXIT_MS = 150;
/** Match {@link KinThinkingDots} / Kin avatar row height for vertical centering. */
export const KIN_TOOL_PROGRESS_ROW_MIN_H = "32px";

/**
 * Same mint steps as {@link KinThinkingDots}, so the active glyph keeps shifting
 * color (not only opacity) and does not read as a static "done" chip.
 */
const kinToolProgressColorPulse = keyframes`
  0%, 100% {
    background-color: ${ADVISOR_KIN_MINT_400};
    opacity: 0.72;
  }
  33% {
    background-color: ${ADVISOR_KIN_MINT_200};
    opacity: 1;
  }
  66% {
    background-color: ${ADVISOR_KIN_MINT_100};
    opacity: 0.85;
  }
`;

type DisplayItem = ToolProgressItem & { exiting?: boolean };

function rowLabel(item: ToolProgressItem): string {
  if (item.status === "failed") return FAILED_COPY;
  if (item.status === "completed") return item.completedLabel;
  return item.label;
}

function rowColor(item: ToolProgressItem): string {
  if (item.status === "failed") return FAILED_ACCENT;
  return ADVISOR_PANEL_NEUTRAL_250;
}

function itemKey(item: ToolProgressItem | null): string {
  if (!item) return "";
  return `${item.toolCallId}:${item.status}`;
}

/**
 * Keeps the prior status mounted briefly so opacity can crossfade into the replacement.
 */
function useDisplayToolProgressItem(
  item: ToolProgressItem | null,
): DisplayItem[] {
  const [display, setDisplay] = useState<DisplayItem[]>(() =>
    item ? [{ ...item }] : [],
  );
  const liveRef = useRef(item);
  liveRef.current = item;
  const key = itemKey(item);

  useEffect(() => {
    const live = liveRef.current;

    setDisplay((prev) => {
      if (!live) {
        return prev.map((row) => ({ ...row, exiting: true }));
      }

      const next: DisplayItem[] = [];
      let sawLive = false;
      for (const row of prev) {
        if (row.toolCallId === live.toolCallId) {
          next.push({ ...live, exiting: false });
          sawLive = true;
          continue;
        }
        next.push({ ...row, exiting: true });
      }
      if (!sawLive) {
        next.push({ ...live, exiting: false });
      }
      return next;
    });

    const timeoutId = window.setTimeout(() => {
      setDisplay((prev) => {
        const liveNow = liveRef.current;
        if (!liveNow) return [];
        return prev.filter((row) => row.toolCallId === liveNow.toolCallId);
      });
    }, KIN_TOOL_PROGRESS_EXIT_MS);

    return () => window.clearTimeout(timeoutId);
  }, [key]);

  return display;
}

function ActivePrefix() {
  return (
    <Box
      as="span"
      aria-hidden
      data-testid="kin-tool-progress-active-glyph"
      marginInlineEnd="8px"
      display="inline-block"
      boxSize="7px"
      borderRadius="full"
      bg={ADVISOR_KIN_MINT_400}
      verticalAlign="middle"
      css={{
        animation: `${kinToolProgressColorPulse} 1.4s ease-in-out infinite`,
        "@media (prefers-reduced-motion: reduce)": {
          animation: "none",
          opacity: 0.72,
          backgroundColor: ADVISOR_KIN_MINT_400,
        },
      }}
    />
  );
}

function StaticPrefix({ item }: { item: ToolProgressItem }) {
  const isCompleted = item.status === "completed";
  return (
    <Box
      as="span"
      aria-hidden
      marginInlineEnd="8px"
      display="inline-block"
      color={isCompleted ? ADVISOR_PANEL_PRIMARY_500 : FAILED_ACCENT}
      fontSize="12px"
      lineHeight="18px"
      verticalAlign="middle"
    >
      {isCompleted ? "✓" : "!"}
    </Box>
  );
}

function ProgressRow({ item }: { item: DisplayItem }) {
  return (
    <Text
      fontSize="12px"
      fontWeight="normal"
      lineHeight="18px"
      color={rowColor(item)}
      data-status={item.status}
      whiteSpace="nowrap"
      overflow="hidden"
      textOverflow="ellipsis"
    >
      {item.status === "active" ? (
        <ActivePrefix />
      ) : (
        <StaticPrefix item={item} />
      )}
      {rowLabel(item)}
    </Text>
  );
}

/**
 * Single Kin-aligned status line. New tool calls replace the previous label in place
 * (no stacked rows). Vertically centered to the 32px Kin avatar like {@link KinThinkingDots}.
 */
export function AgentToolProgress({ items }: AgentToolProgressProps) {
  const primary = primaryToolProgressItem(items);
  const displayItems = useDisplayToolProgressItem(primary);
  if (!displayItems.length) return null;

  const liveItem = displayItems.find((item) => !item.exiting) ?? null;
  const liveText =
    liveItem?.status === "active" ? `${rowLabel(liveItem)}.` : undefined;
  const busy = liveItem?.status === "active";

  return (
    <Box
      aria-busy={busy || undefined}
      data-testid="agent-tool-progress"
      minH={KIN_TOOL_PROGRESS_ROW_MIN_H}
      display="flex"
      alignItems="center"
      w="100%"
    >
      {liveText ? (
        <VisuallyHidden aria-live="polite" aria-atomic="true">
          {liveText}
        </VisuallyHidden>
      ) : null}
      <Box display="grid" w="100%" alignItems="center" minH="18px">
        {displayItems.map((item) => (
          <Box
            key={item.toolCallId}
            gridArea="1 / 1"
            data-exiting={item.exiting || undefined}
            opacity={item.exiting ? 0 : 1}
            transition={`opacity ${KIN_TOOL_PROGRESS_EXIT_MS}ms ease-out`}
            pointerEvents={item.exiting ? "none" : undefined}
            aria-hidden={item.exiting || undefined}
            overflow="hidden"
            css={{
              "@media (prefers-reduced-motion: reduce)": {
                transition: "none",
              },
            }}
          >
            <ProgressRow item={item} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}
